import {
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Chart,
    Convertshape,
    Convertshape2,
    Trash,
} from '@/components/icons';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { TABLE_CELL_COLORS } from '@/lib/table-cell-color';
import { cn } from '@/lib/utils';
import type { TableCellColor } from '@/types/report-payload';
import type { ReactNode } from 'react';

interface TableContextMenuProps {
    children: ReactNode;
    disabled: boolean;
    canMerge: boolean;
    canUnmerge: boolean;
    canInsertRow: boolean;
    canDeleteRow: boolean;
    canInsertColumn: boolean;
    canDeleteColumn: boolean;
    onOpenChange?: (open: boolean) => void;
    onMerge: () => void;
    onUnmerge: () => void;
    onInsertRowAbove: () => void;
    onInsertRowBelow: () => void;
    onDeleteRow: () => void;
    onInsertColumnLeft: () => void;
    onInsertColumnRight: () => void;
    onDeleteColumn: () => void;
    onAddChart?: () => void;
    /**
     * Color applied to the selection, so the strip can mark it. 'mixed' when the
     * selected cells do not share one, and nothing gets marked.
     */
    currentColor?: TableCellColor | 'mixed';
    onColorChange: (color?: TableCellColor) => void;
}

export function TableContextMenu({
    children,
    disabled,
    canMerge,
    canUnmerge,
    canInsertRow,
    canDeleteRow,
    canInsertColumn,
    canDeleteColumn,
    onOpenChange,
    onMerge,
    onUnmerge,
    onInsertRowAbove,
    onInsertRowBelow,
    onDeleteRow,
    onInsertColumnLeft,
    onInsertColumnRight,
    onDeleteColumn,
    onAddChart,
    currentColor,
    onColorChange,
}: TableContextMenuProps) {
    return (
        <ContextMenu onOpenChange={onOpenChange}>
            {/* Disabling the trigger — not just the items — is what keeps a
                read-only table (a review rehearsal, a finalized laudo) from
                answering a right-click with a menu greyed out end to end. */}
            <ContextMenuTrigger asChild disabled={disabled}>
                {children}
            </ContextMenuTrigger>
            <ContextMenuContent>
                {/* Each chip is the letter painted in its own color: what it
                    does is drawn, not written. */}
                <div className="flex gap-1 p-1" role="group" aria-label="Cor do texto">
                    <ColorSwatch
                        label="Cor padrão"
                        color="#0f172a"
                        isActive={!currentColor}
                        disabled={disabled}
                        onSelect={() => onColorChange(undefined)}
                    />
                    {TABLE_CELL_COLORS.map((option) => (
                        <ColorSwatch
                            key={option.key}
                            label={option.label}
                            color={option.hex}
                            isActive={currentColor === option.key}
                            disabled={disabled}
                            onSelect={() => onColorChange(option.key)}
                        />
                    ))}
                </div>
                <ContextMenuSeparator />
                <ContextMenuItem onSelect={onMerge} disabled={disabled || !canMerge}>
                    <div className="flex items-center gap-2">
                        <Convertshape className="size-4 text-slate-500" />
                        Mesclar células
                    </div>
                </ContextMenuItem>
                <ContextMenuItem onSelect={onUnmerge} disabled={disabled || !canUnmerge}>
                    <div className="flex items-center gap-2">
                        <Convertshape2 className="size-4 text-slate-500" />
                        Desfazer mesclagem
                    </div>
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onSelect={onInsertRowAbove} disabled={disabled || !canInsertRow}>
                    <div className="flex items-center gap-2">
                        <ArrowUp className="size-4 text-slate-500" />
                        Inserir linha acima
                    </div>
                </ContextMenuItem>
                <ContextMenuItem onSelect={onInsertRowBelow} disabled={disabled || !canInsertRow}>
                    <div className="flex items-center gap-2">
                        <ArrowDown className="size-4 text-slate-500" />
                        Inserir linha abaixo
                    </div>
                </ContextMenuItem>
                <ContextMenuItem onSelect={onDeleteRow} disabled={disabled || !canDeleteRow}>
                    <div className="flex items-center gap-2">
                        <Trash className="size-4 text-slate-500" />
                        Excluir linha
                    </div>
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem
                    onSelect={onInsertColumnLeft}
                    disabled={disabled || !canInsertColumn}
                >
                    <div className="flex items-center gap-2">
                        <ArrowLeft className="size-4 text-slate-500" />
                        Inserir coluna à esquerda
                    </div>
                </ContextMenuItem>
                <ContextMenuItem
                    onSelect={onInsertColumnRight}
                    disabled={disabled || !canInsertColumn}
                >
                    <div className="flex items-center gap-2">
                        <ArrowRight className="size-4 text-slate-500" />
                        Inserir coluna à direita
                    </div>
                </ContextMenuItem>
                <ContextMenuItem onSelect={onDeleteColumn} disabled={disabled || !canDeleteColumn}>
                    <div className="flex items-center gap-2">
                        <Trash className="size-4 text-slate-500" />
                        Excluir coluna
                    </div>
                </ContextMenuItem>
                {onAddChart && (
                    <>
                        <ContextMenuSeparator />
                        <ContextMenuItem onSelect={onAddChart} disabled={disabled}>
                            <div className="flex items-center gap-2">
                                <Chart className="size-4 text-slate-500" />
                                Adicionar gráfico desta tabela
                            </div>
                        </ContextMenuItem>
                    </>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
}

interface ColorSwatchProps {
    label: string;
    color: string;
    isActive: boolean;
    disabled: boolean;
    onSelect: () => void;
}

function ColorSwatch({ label, color, isActive, disabled, onSelect }: ColorSwatchProps) {
    return (
        <ContextMenuItem
            onSelect={onSelect}
            disabled={disabled}
            title={label}
            aria-label={label}
            className={cn(
                'size-7 justify-center rounded-full border border-border bg-background p-0 text-sm font-bold leading-none',
                'focus:bg-muted focus:text-current',
                isActive && 'border-foreground',
            )}
            style={{ color }}
        >
            A
        </ContextMenuItem>
    );
}
